import React, { useState, useEffect } from "react";
import { useSENSAI } from "./SENSAIProvider";
import { SENSAIPageContent } from "./types";
import { TeachTab } from "./components/TeachTab";
import { VerifyTab } from "./components/VerifyTab";
import { ActivityTab } from "./components/ActivityTab";
import { ChatTab } from "./components/ChatTab";
import { SENSAIPanelChatInput } from "./components/SENSAIPanelChatInput";

interface SENSAIPanelProps {
  onClose: () => void;
  initialTab?: "teach" | "verify" | "activity" | "chat";
}

export const SENSAIPanel: React.FC<SENSAIPanelProps> = ({ onClose, initialTab = "teach" }) => {
  const context = useSENSAI();
  const [tab, setTab] = useState<"teach" | "verify" | "activity" | "chat">(initialTab);
  const [content, setContent] = useState<SENSAIPageContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/sensai/content/${context.pageId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const pageContent = await response.json();
          setContent(pageContent);
        } else {
          console.warn(`No content found for page: ${context.pageId}`);
          setContent(null);
        }
      } catch (error) {
        console.error("Failed to load SENSAI content:", error);
        setContent(null);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 5000);

    loadContent().finally(() => {
      clearTimeout(timeoutId);
    });

    return () => clearTimeout(timeoutId);
  }, [context.pageId]);

  const filteredContent = content ? {
    ...content,
    teach: {
      ...content.teach,
      steps: content.teach.steps.filter(step => 
        step.visibleTo.includes(context.userRole)
      ),
    },
    verify: {
      checks: content.verify.checks.filter(check => 
        check.visibleTo.includes(context.userRole)
      ),
    },
    actions: content.actions.filter(action => 
      action.visibleTo.includes(context.userRole)
    ),
  } : null;

  return (
    <div className="sensai-panel fixed top-0 right-0 h-full w-[600px] grid grid-rows-[auto_1fr_auto] z-[9999] bg-slate-900/95 backdrop-blur-sm border-l border-slate-700/50">
      <div className="p-6 flex items-center justify-between border-b border-slate-700/50">
        <div className="flex flex-col">
          <div className="font-bold text-white text-xl tracking-wide">
            SENSAI
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {(["teach", "verify", "activity", "chat"] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all ${
                tab === t
                  ? "bg-purple-600 text-white shadow-sm"
                  : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 hover:text-white"
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
          <button
            onClick={onClose}
            className="ml-3 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="sensai-content p-6 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-slate-400 font-medium">Loading SENSAI...</div>
          </div>
        ) : (
          <>
            {tab === "teach" && <TeachTab content={filteredContent} context={context} />}
            {tab === "verify" && <VerifyTab content={filteredContent} context={context} />}
            {tab === "activity" && <ActivityTab context={context} />}
            {tab === "chat" && <ChatTab context={context} />}
          </>
        )}
      </div>
      <SENSAIPanelChatInput context={context} />
    </div>
  );
};
